/**
 * Skeleton Component - Loading placeholder with shimmer effect
 * 
 * @param {Object} props - Component props
 * @param {string} props.variant - Skeleton variant: 'text', 'circular', 'rectangular', 'card'
 * @param {string} props.width - Width of skeleton (CSS value)
 * @param {string} props.height - Height of skeleton (CSS value)
 * @param {string} props.className - Additional CSS classes
 */
const Skeleton = ({
  variant = 'text',
  width,
  height,
  className = '',
}) => {
  // Variant classes
  const variantClasses = {
    text: 'h-4 rounded',
    circular: 'rounded-full',
    rectangular: 'rounded-lg',
    card: 'rounded-lg h-32',
    avatar: 'w-10 h-10 rounded-full',
    button: 'h-10 w-24 rounded-lg',
    input: 'h-10 rounded-lg',
    image: 'rounded-lg aspect-video',
  };

  const style = {
    width: width,
    height: height,
  };

  return (
    <div
      className={`skeleton ${variantClasses[variant]} ${className}`}
      style={style}
    />
  );
};

/**
 * SkeletonText Component - Multiple text lines skeleton
 */
export const SkeletonText = ({ 
  lines = 3, 
  lastLineWidth = '60%',
  className = '' 
}) => {
  return (
    <div className={`space-y-2 ${className}`}>
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton
          key={i}
          variant="text"
          width={i === lines - 1 ? lastLineWidth : '100%'}
        />
      ))}
    </div>
  );
};

/**
 * SkeletonCard Component - Full card skeleton
 */
export const SkeletonCard = ({ className = '' }) => {
  return (
    <div className={`card ${className}`}>
      <div className="animate-pulse space-y-4">
        <Skeleton variant="text" width="60%" />
        <Skeleton variant="text" width="100%" />
        <Skeleton variant="text" width="80%" />
        <div className="flex space-x-2 pt-2">
          <Skeleton variant="button" />
          <Skeleton variant="button" width="80px" />
        </div>
      </div>
    </div>
  );
};

/**
 * SkeletonTable Component - Table skeleton
 */
export const SkeletonTable = ({ 
  rows = 5, 
  columns = 4,
  className = '' 
}) => {
  return (
    <div className={`border border-gray-200 rounded-lg overflow-hidden ${className}`}>
      {/* Header */}
      <div className="bg-gray-50 px-6 py-3 flex space-x-4">
        {Array.from({ length: columns }).map((_, i) => (
          <Skeleton key={i} variant="text" width={`${100 / columns}%`} />
        ))}
      </div>
      
      {/* Rows */}
      <div className="divide-y divide-gray-200">
        {Array.from({ length: rows }).map((_, rowIndex) => (
          <div key={rowIndex} className="px-6 py-4 flex space-x-4">
            {Array.from({ length: columns }).map((_, colIndex) => (
              <Skeleton 
                key={colIndex} 
                variant="text" 
                width={`${100 / columns}%`}
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

/**
 * SkeletonList Component - List skeleton
 */
export const SkeletonList = ({ 
  items = 5, 
  showAvatar = false,
  className = '' 
}) => {
  return (
    <div className={`space-y-4 ${className}`}>
      {Array.from({ length: items }).map((_, i) => (
        <div key={i} className="flex items-center space-x-4 animate-pulse">
          {showAvatar && <Skeleton variant="avatar" />}
          <div className="flex-1 space-y-2">
            <Skeleton variant="text" width="50%" />
            <Skeleton variant="text" width="80%" />
          </div>
        </div>
      ))}
    </div>
  );
};

/**
 * SkeletonStat Component - Stat card skeleton
 */
export const SkeletonStat = ({ className = '' }) => {
  return (
    <div className={`card ${className}`}>
      <div className="flex items-center space-x-4 animate-pulse">
        <Skeleton variant="circular" width="48px" height="48px" />
        <div className="flex-1 space-y-2">
          <Skeleton variant="text" width="60%" />
          <Skeleton variant="text" width="40%" height="24px" />
        </div>
      </div>
    </div>
  );
};

/**
 * SkeletonGrid Component - Grid of skeleton cards
 */
export const SkeletonGrid = ({ 
  count = 4, 
  columns = 2,
  className = '' 
}) => {
  const gridCols = {
    1: 'grid-cols-1',
    2: 'grid-cols-1 sm:grid-cols-2',
    3: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
    4: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4',
  };

  return (
    <div className={`grid ${gridCols[columns]} gap-6 ${className}`}>
      {Array.from({ length: count }).map((_, i) => (
        <SkeletonCard key={i} />
      ))}
    </div>
  );
};

export default Skeleton;